<template>
  <div>
    <h1>
      Профіль
      <span v-if="user && user.role == 'admin'"> адміністратора </span>
      <span v-else> користувача </span>
    </h1>
    <div class="meta mb-1 pt-2 d-flex">
      <div style="margin-right: 24px">
        ID: <strong>{{ user.id }}</strong>
      </div>
      <div style="margin-right: 24px">
        Створено:
        <strong v-if="user.created_at">{{
          user.created_at.seconds | moment("calendar")
        }}</strong>
        <strong v-else>--</strong>
      </div>
      <div style="margin-right: 24px">
        Останній візит:
        <strong v-if="user.last_visit">{{
          user.last_visit.seconds | moment("calendar")
        }}</strong>
        <strong v-else>--</strong>
      </div>
    </div>
    <div class="border mb-1"></div>
    <div class="py-2 controls mb-1">
      <b-button
        size="sm"
        style="margin-right: 20px"
        :href="'tel:' + user.phone"
        variant="warning"
        :disabled="!user.phone"
      >
        <b-icon-telephone-fill /> Зателефонувати
      </b-button>
      <b-button
        size="sm"
        style="margin-right: 20px"
        @click="openSmsModal"
        variant="success"
      >
        <b-icon-chat-left-dots-fill /> Написати СМС
      </b-button>
      <b-button
        size="sm"
        style="margin-right: 20px"
        @click="openTelegramModal"
        variant="success"
        class="telegram"
        v-if="user.telegram_chat_id"
      >
        <b-icon-chat-fill /> Написати в Telegram
      </b-button>
      <b-button
        size="sm"
        style="margin-right: 20px"
        :href="'tel:' + user.phone"
        variant="link"
        v-if="!user.activated && !user.role == 'admin'"
      >
        <b-icon-envelope-fill /> Надіслати запрошення повторно
      </b-button>
    </div>
    <b-card>
      <b-overlay :show="isLoading" spinner-variant="primary">
        <h3>Загальна інформація</h3>
        <div class="form-input mb-3">
          <label for=""> Імʼя </label>
          <b-form-input placeholder="Імʼя" v-model="user.first_name" />
        </div>
        <div class="form-input mb-3">
          <label for=""> Прізвище </label>
          <b-form-input placeholder="Прізвище" v-model="user.last_name" />
        </div>
        <div class="form-input mb-3">
          <label for=""> Номер телефону </label>
          <b-form-input
            disabled
            placeholder="Номер телефону"
            v-model="user.phone"
          />
        </div>
        <div class="form-input">
          <label for=""> E-mail </label>
          <b-form-input disabled placeholder="E-mail" v-model="user.email" />
        </div>
      </b-overlay>
    </b-card>
    <b-card class="mt-4">
      <h3>Теги та категорії</h3>
      <b-overlay :show="isLoading" spinner-variant="primary">
        <div class="form-input mb-4">
          <label for=""> <b-icon-tag /> Теги </label>
          <v-select
            :options="tagOptions"
            :reduce="(option) => option.value"
            v-model="user.tags"
            multiple
          />
        </div>
        <div class="form-input">
          <label for=""> <b-icon-folder /> Категорії </label>
          <v-select
            :options="categoryOptions"
            :reduce="(option) => option.value"
            v-model="user.category"
          />
        </div>
      </b-overlay>
    </b-card>
    <b-card title="Соц. мережі" class="mt-4">
      <b-overlay :show="isLoading" spinner-variant="primary">
        <div class="form-input mt-1">
          <label for=""> <b-icon-facebook /> Facebook </label>
          <b-form-input
            placeholder="Профіль у Facebook"
            v-model="user.facebook"
          />
        </div>
        <div class="form-input mt-1">
          <label for=""> <b-icon-linkedin /> LinkedIn </label>
          <b-form-input
            placeholder="Профіль у LinkedIn"
            v-model="user.linkedin"
          />
        </div>
      </b-overlay>
    </b-card>
    <b-card class="mt-4">
      <h3>Резюме</h3>
      <b-overlay :show="isLoading" spinner-variant="primary">
        <div v-if="user.resume_url" class="d-flex">
          <a :href="user.resume_url">{{ user.resume_filename }}</a>
          <div class="controls" style="margin-left: auto">
            <b-button
              :href="user.resume_url"
              size="sm"
              variant="outline-primary"
              target="_blank"
            >
              <b-icon-download />
              Завантажити
            </b-button>
          </div>
        </div>

        <div class="no-data py-4 my-4 text-center" v-else>
          <h5 class="mb-4">Користувач не завантажив резюме</h5>
          <img width="160" src="@/assets/img/no-data-alt.svg" alt="" />
        </div>
      </b-overlay>
    </b-card>
    <b-card class="mt-4 mb-4">
      <b-overlay :show="isLoading" spinner-variant="primary">
        <h3>Результати тестів</h3>
        <div class="results" v-if="test_results.length && !isLoading">
          <div
            class="results__item"
            v-for="r in test_results"
            :key="r.datetime.seconds"
          >
            <router-link
              :to="{ name: 'test-single', params: { id: r.test_id } }"
            >
              {{ r.test_name }}
            </router-link>

            <div class="meta my-1 d-flex align-items-center">
              <div class="date">
                {{ r.datetime.seconds | moment("calendar") }}
              </div>
              <div class="points">
                Набрано балів:
                <strong> {{ r.scores ? r.scores : "немає" }}</strong>
              </div>
            </div>
            <p v-html="r.text"></p>
          </div>
        </div>
        <div class="no-data py-4 my-4 text-center" v-else>
          <h5 class="mb-4">Поки немає даних</h5>
          <img width="160" src="@/assets/img/no-data-alt.svg" alt="" />
        </div>
      </b-overlay>
    </b-card>
    <b-card class="mt-4 mb-4">
      <h3>Анкетування</h3>
      <div v-for="item in infoItems" :key="item">
        <template>
          {{ item.key }} : <strong>{{ item.value }}</strong>
        </template>
      </div>
    </b-card>
    <b-card class="mt-4 mb-4">
      <h3>Нотатки</h3>
      <b-form-textarea
        rows="5"
        placeholder="Швидкі нотатки"
        v-model="user.notes"
      ></b-form-textarea>
    </b-card>
    <div class="d-flex justify-content-between mt-3 mb-4">
      <b-button variant="outline-danger" @click="startRemoving">
        Видалити користувача
      </b-button>
      <b-button
        variant="primary"
        size="md"
        @click="saveUser"
        :disabled="isLoading"
      >
        <b-icon-check />
        Зберегти зміни
      </b-button>
    </div>
    <b-modal v-model="showRemoveModal" centered hide-footer hide-header>
      <b-overlay :show="modalLoading" spinner-variant="danger">
        <h3 class="mb-4">Справді бажаєте видалити користувача?</h3>
        <b-alert variant="danger" show>
          УВАГА! Всі дані було видалено назавжди
        </b-alert>
        <div class="w-100 mt-4">
          <b-button variant="danger" class="w-100 mb-2" @click="removeUser">
            Видалити користувача
          </b-button>
          <b-button
            variant="outline-danger"
            class="w-100 mb-2"
            @click="showRemoveModal = false"
          >
            Скасувати
          </b-button>
        </div>
      </b-overlay>
    </b-modal>
    <sendSmsModal ref="userSmsModal" :user="user" />
    <sendTelegranModal ref="userTelegramModal" :user="user" />
  </div>
</template>

<script>
import sendSmsModal from "@/components/admin/modals/send-sms.vue";
import sendTelegranModal from "@/components/admin/modals/send-telegram.vue";
export default {
  components: {
    sendSmsModal,
    sendTelegranModal,
  },
  data() {
    return {
      isLoading: false,
      test_results: [],
      modalLoading: false,
      user: {},
      all_tags: [],
      showRemoveModal: false,
      categories: [],
    };
  },
  watch: {
    $route: {
      handler() {
        this.fetchUser();
      },
    },
  },

  computed: {
    infoItems() {
      let items = [];
      if (this.user.info) {
        for (var key in this.user.info) {
          items.push({
            key: key,
            value: this.user.info[key],
          });
        }
      }
      return items;
    },
    tagOptions() {
      let options = [];
      if (this.all_tags.length) {
        options = this.all_tags.map((el) => {
          return {
            label: el.name,
            value: el.id,
          };
        });
      }

      return options;
    },
    categoryOptions() {
      let options = [];
      if (this.categories.length) {
        options = this.categories.map((el) => {
          return {
            label: el.name,
            value: el.id,
          };
        });
      }

      return options;
    },
  },
  methods: {
    openSmsModal() {
      this.$refs.userSmsModal.show = true;
    },
    openTelegramModal() {
      this.$refs.userTelegramModal.show = true;
    },
    async removeUser() {
      this.modalLoading = true;

      await this.$api
        .get("/user/delete/?uid=" + this.user.uid)
        .then((response) => {
          console.log(response);
          if (response.data.code == "success") {
            this.$toast.warning("Користувача успішно видалено");
            this.$router.push({
              name: "admin-users",
            });
          }
          this.modalLoading = false;
        })
        .catch((error) => {
          console.log(error);
          this.$toast.error("Щось пішло не так");
          this.modalLoading = false;
        });
    },
    startRemoving() {
      this.showRemoveModal = true;
    },
    async saveUser() {
      this.isLoading = true;
      let newData = Object.assign({}, this.user);
      delete newData["uid"];

      let result = await this.$db.update("users", this.user.uid, newData);

      console.log(result);
      if (result.success) {
        this.$toast.success("Зміни збережено");
      }

      this.isLoading = false;
    },
    async fetchCategories() {
      this.isLoading = true;
      let args = {
        collectionName: "categories",
      };
      let result = await this.$db.getCollection(args);
      this.categories = result.docs;
      this.isLoading = false;
    },
    async fetchTags() {
      this.isLoading = true;
      let args = {
        collectionName: "tags",
      };
      let result = await this.$db.getCollection(args);
      this.all_tags = result.docs;
      this.isLoading = false;
    },
    async fetchUser() {
      this.isLoading = true;

      let args = {
        collectionName: "users",
        where: ["id", "==", Number(this.$route.params.id)],
      };
      let result = await this.$db.getCollection(args);
      if (result.docs.length) {
        this.user = result.docs[0];
        if (!this.user.facebook) {
          this.user.facebook = "";
        }
        if (!this.user.linkedin) {
          this.user.linkedin = "";
        }
        this.fetchResults();
      }

      this.isLoading = false;
    },
    async fetchResults() {
      this.isLoading = true;

      let args = {
        collectionName: "test_results",
        where: ["user_id", "==", Number(this.$route.params.id)],
        order: ["datetime", "desc"],
      };
      let result = await this.$db.getCollection(args);
      if (result.docs.length) {
        this.test_results = result.docs;
      }

      this.isLoading = false;
    },
  },
  mounted() {
    this.fetchCategories();
    this.fetchTags();
    this.fetchUser();
  },
};
</script>

<style lang="scss" scoped>
.meta {
  color: #777;
  font-size: 13px;
  strong {
    color: #666;
  }
}
.controls {
  .btn {
    svg {
      margin-right: 10px;
    }
    &.telegram {
      background: #0088cc;
      border-color: #0088cc;
    }
  }
}
.results {
  position: relative;
  margin: 20px 0px;
  &::before {
    display: block;
    width: 1px;
    height: 100%;
    bottom: 10px;
    top: 14px;
    left: 10px;
    content: "";
    position: absolute;
    background: var(--primary-color);
  }
  &__item {
    padding: 10px 10px 20px 40px;
    &:last-child {
      padding-bottom: 0;
    }
    .date {
      background: var(--secondary-color);
      display: inline-block;
      color: var(--primary-color);
      font-size: 12px;
      padding: 3px 6px;
      border-radius: 4px;
      font-weight: 600;
      line-height: 1;
      margin-right: 20px;
    }
    a {
      font-size: 14px;
      line-height: 1;
      color: var(--primary-color);
      font-weight: 600;
      position: relative;
      &::before {
        display: block;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        left: -38px;
        top: 0px;
        content: "";
        position: absolute;
        background: var(--primary-color);
      }
    }
    p {
      font-size: 13px;
      margin-bottom: 0px;
    }
  }
}
</style>
